import React, { useState, useEffect } from 'react';
import { useGame } from '../context/GameContext.jsx';
import logoCountdown from '../../assets/images/logo-white.png';
import logoFantasy from '../../assets/images/logo-fantasy.png';

const InitialScreen = () => {
  const {
    createSession,
    translations,
    lang,
    setLang,
    isLoading,
    validateCodeAndFetchLanguages,
    availableLanguages,
    setError,
    missionCode,
    sessionId,
    showAlertModal,
    navigateToMissionScreen,
    fetchSessionStatus,
    uiTheme
  } = useGame();

  const [fetchedSessionStatus, setFetchedSessionStatus] = useState(null);
  const [fetchedSessionCode, setFetchedSessionCode] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlMissionCode = params.get('c');

    const validateAndSetCode = async (mCode) => {
      if (mCode) {
        const success = await validateCodeAndFetchLanguages(mCode);
        if (success && availableLanguages.length === 1) {
          handleLanguageChange(availableLanguages[0]);
        }
      } else {
        setError(translations[lang]?.initial_screen_subtitle);
      }
    };

    const checkExistingSession = async (currentUrlMissionCode) => {
      const storedSessionId = localStorage.getItem('sessionId');
      if (storedSessionId) {
        try {
          const sessionResponse = await fetchSessionStatus(storedSessionId, false, currentUrlMissionCode);
          if (sessionResponse) {
            setFetchedSessionStatus(sessionResponse.Status);
            setFetchedSessionCode(sessionResponse.Code);
          }
        } catch (err) {
          console.error("Error checking existing session:", err);
        }
      }
    };

    validateAndSetCode(urlMissionCode);
    checkExistingSession(urlMissionCode);
  }, [lang, translations, validateCodeAndFetchLanguages, setError, fetchSessionStatus]);

  useEffect(() => {
    if (availableLanguages.length === 1 && lang !== availableLanguages[0]) {
      handleLanguageChange(availableLanguages[0]);
    }
  }, [availableLanguages, lang]);

  const handleLanguageChange = (newLang) => {
    if (translations[newLang] && newLang !== lang) {
      setLang(newLang);
      localStorage.setItem('language', newLang);
      if (missionCode) {
        validateCodeAndFetchLanguages(missionCode);
      }
    }
  };

  const isLanguageAvailable = (languageCode) => {
    if (!missionCode || availableLanguages.length === 0) return true;
    return availableLanguages.includes(languageCode);
  };

  const handleCreateSessionClick = async () => {
    if (sessionId) {
      showAlertModal(
        translations[lang]?.confirm_new_game_title,
        translations[lang]?.confirm_new_game_message,
        true,
        async () => {
          await createSession();
          navigateToMissionScreen();
        },
        () => {},
        translations[lang]?.confirm_new_game_yes,
        translations[lang]?.confirm_new_game_no
      );
    } else {
      await createSession();
      navigateToMissionScreen();
    }
  };

  const actionButtonDisabled = isLoading || availableLanguages.length === 0;
  const logoSrc = uiTheme === 'fantasy' ? logoFantasy : logoCountdown; // Placeholder for fantasy logo

  return (
    <div className="initial-screen-container screen-content">
      <img src={logoSrc} alt={translations[lang]?.game_logo_alt} className="game-logo" />
      <p className="initial-screen-subtitle">
        {missionCode
          ? (availableLanguages.length > 0
            ? (translations[lang]?.scanned_code_ready?.replace('{0}', missionCode))
            : (translations[lang]?.error_code_not_found))
          : (translations[lang]?.initial_screen_subtitle)
        }
      </p>

      <button
        className="btn btn-primary btn-lg initial-screen-button create-session-button"
        onClick={handleCreateSessionClick}
        disabled={actionButtonDisabled}
      >
        {isLoading ? translations[lang]?.loading_text : translations[lang]?.create_session_button}
      </button>

      <div className="mb-3">
        <p>{translations[lang]?.language_label}</p>
        <div className="d-flex justify-content-center gap-3">
          {isLanguageAvailable('BR') && (
            <img
              className={`flag-icon ${lang === 'BR' ? 'active' : ''}`}
              data-lang="BR"
              src="/assets/images/BR-64.png"
              alt={translations[lang]?.brazil_flag_alt}
              title={translations[lang]?.BR_flag_title}
              onClick={() => handleLanguageChange('BR')}
              style={{ pointerEvents: isLoading ? 'none' : 'auto' }}
            />
          )}
          {isLanguageAvailable('EN') && (
            <img
              className={`flag-icon ${lang === 'EN' ? 'active' : ''}`}
              data-lang="EN"
              src="/assets/images/GB-64.png"
              alt={translations[lang]?.uk_flag_alt}
              title={translations[lang]?.EN_flag_title}
              onClick={() => handleLanguageChange('EN')}
              style={{ pointerEvents: isLoading ? 'none' : 'auto' }}
            />
          )}
          {isLanguageAvailable('PL') && (
            <img
              className={`flag-icon ${lang === 'PL' ? 'active' : ''}`}
              data-lang="PL"
              src="/assets/images/PL-64.png"
              alt={translations[lang]?.poland_flag_alt}
              title={translations[lang]?.PL_flag_title}
              onClick={() => handleLanguageChange('PL')}
              style={{ pointerEvents: isLoading ? 'none' : 'auto' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default InitialScreen;
